import { useState } from "react"
import { scrollToSection, CHAPTERS, FAQS, PROMPT_EXAMPLES, TESTIMONIALS, LEARNS, FOR_WHO, BENEFITS } from "@/lib"
import LeadCaptureModal from "@/components/LeadCaptureModal"

/* ── TYPES (aligned with lib/index.ts) ── */
type Chapter = { id: number; icon: string; title: string; color: string }
type Benefit = { metric: string; label: string }
type Learn = { icon: string; title: string; desc: string }
type PromptEx = { category: string; number: string; title: string; prompt: string; result: string }
type Testimonial = { name: string; role: string; city: string; text: string; stars: number }
type Faq = { q: string; a: string }

/* ── ICONS ── */
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M5 13l4 4L19 7" stroke="#00FF88" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const IconChevron = ({ open }: { open: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>
    <path d="M6 9l6 6 6-6" stroke="#00D4FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* ── SECTION LABEL ── */
const SectionLabel = ({ text }: { text: string }) => (
  <div className="flex items-center justify-center mb-4">
    <div className="badge-cyber">
      <span style={{ color: "#00D4FF" }}>//</span> {text}
    </div>
  </div>
)

/* ── SECTION HEADING ── */
const SectionHeading = ({ line1, accent }: { line1: string; accent?: string }) => (
  <div style={{ textAlign: "center" }}>
    <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(1.9rem,4vw,2.8rem)", fontWeight: 700, lineHeight: 1.2, color: "rgba(232,244,253,0.95)" }}>
      {line1}
      {accent && (
        <><br /><span className="text-neon-cyan">{accent}</span></>
      )}
    </h2>
  </div>
)

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false)
  const [faqOpen, setFaqOpen] = useState<number | null>(0)

  return (
    <>
      <LeadCaptureModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      {/* ═══════════════════════════════════════
          01 · HERO
      ═══════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
        style={{ background: "#040810", paddingTop: "5rem", paddingBottom: "5rem" }}>

        {/* Grid + orbs */}
        <div className="absolute inset-0 bg-grid-pattern" />
        <div className="orb-cyan absolute" style={{ width: 600, height: 600, top: "-10%", left: "-12%", opacity: 0.55 }} />
        <div className="orb-purple absolute" style={{ width: 500, height: 500, bottom: "-5%", right: "-10%", opacity: 0.45 }} />
        <div className="orb-amber absolute" style={{ width: 280, height: 280, top: "42%", left: "58%", opacity: 0.22 }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center flex flex-col items-center gap-8">
          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="badge-cyber">
              <span className="animate-pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: "#00FF88", display: "inline-block", boxShadow: "0 0 6px #00FF88" }} />
              SISTEMA IA · AGENTES DE VIAJES
            </div>
            <div className="badge-gold">Ed. 2026</div>
          </div>

          {/* Headline */}
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(9px,1.4vw,12px)", letterSpacing: "0.2em", color: "rgba(0,212,255,0.65)", marginBottom: "1rem", textTransform: "uppercase" }}>
              &gt;_ inicializar protocolo de ventas con IA...
            </div>
            <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.4rem,6vw,4.5rem)", fontWeight: 700, lineHeight: 1.1, color: "rgba(232,244,253,0.97)" }}>
              <span className="text-neon-cyan">100 Prompts Maestros</span>
              <br />para Agentes de Viajes
            </h1>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(1rem,2vw,1.35rem)", color: "rgba(139,92,246,0.9)", marginTop: "0.75rem", letterSpacing: "0.05em" }}>
              Edición 2026
            </div>
          </div>

          {/* Sub */}
          <p style={{ fontSize: "clamp(1rem,2vw,1.15rem)", color: "rgba(148,163,184,0.82)", maxWidth: 600, lineHeight: 1.7 }}>
            El sistema de prompts con IA más completo del mercado hispanohablante para transformar tu agencia de viajes en una máquina de ventas inteligente.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full justify-center">
            <button className="btn-primary-glow animate-pulse-glow" style={{ minWidth: 260 }}
              onClick={() => setModalOpen(true)}>
              ⚡ Descargar Gratis Ahora
            </button>
            <button className="btn-cyber" style={{ minWidth: 200 }}
              onClick={() => scrollToSection("incluye")}>
              Ver qué incluye <IconArrow />
            </button>
          </div>

          {/* Social proof mini */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[{ v: "+2,800", l: "Agentes activos" }, { v: "4.9★", l: "Calificación" }, { v: "100%", l: "Descarga inmediata" }].map(s => (
              <div key={s.v} className="flex items-center gap-2 px-4 py-2 rounded-lg"
                style={{ background: "rgba(0,212,255,0.06)", border: "1px solid rgba(0,212,255,0.12)" }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700, color: "#00D4FF" }}>{s.v}</span>
                <span style={{ fontSize: 12, color: "rgba(148,163,184,0.65)" }}>{s.l}</span>
              </div>
            ))}
          </div>

          {/* Terminal preview */}
          <div className="terminal-window w-full max-w-xl animate-float" style={{ textAlign: "left" }}>
            <div className="terminal-bar">
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840" }} />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(0,212,255,0.5)", marginLeft: 8 }}>prompt_maestro_001.txt</span>
            </div>
            <div className="terminal-body" style={{ fontSize: 11 }}>
              <div style={{ color: "rgba(0,255,136,0.45)" }}># Prompt de ventas para crucero premium</div>
              <div style={{ color: "#00D4FF" }}>AGENTE_IA&gt;</div>
              <div style={{ marginLeft: 16, color: "#00FF88" }}>Actúa como consultor senior en turismo de lujo.</div>
              <div style={{ marginLeft: 16, color: "#00FF88" }}>Crea una propuesta para crucero Mediterráneo</div>
              <div style={{ marginLeft: 16, color: "#00FF88" }}>enfocada en ROI emocional y cierre de venta.</div>
              <div style={{ color: "rgba(0,212,255,0.4)", marginTop: 8 }}>✓ Ejecutando... prompt optimizado listo</div>
            </div>
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          02 · STATS
      ═══════════════════════════════════════ */}
      <section style={{ background: "#070D1A", padding: "4rem 1rem" }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {([
              { num: "100", label: "Prompts especializados", color: "#00D4FF" },
              { num: "20",  label: "Capítulos temáticos",   color: "#8B5CF6" },
              { num: "2800+",label: "Agentes en 12 países", color: "#00FF88" },
              { num: "100%", label: "Descarga inmediata",   color: "#F59E0B" },
            ] as const).map(s => (
              <div key={s.label} className="cyber-card p-6 text-center flex flex-col gap-2">
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(1.8rem,4.5vw,3rem)", fontWeight: 700, color: s.color, lineHeight: 1, textShadow: `0 0 20px ${s.color}55` }}>{s.num}</div>
                <div style={{ fontSize: 11, color: "rgba(148,163,184,0.65)", letterSpacing: "0.03em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          03 · EL PROBLEMA
      ═══════════════════════════════════════ */}
      <section style={{ background: "#040810", padding: "6rem 1rem" }}>
        <div className="max-w-4xl mx-auto px-4">
          <SectionLabel text="DIAGNÓSTICO DEL SECTOR" />
          <SectionHeading line1="¿Tu agencia de viajes" accent="está dejando dinero en la mesa?" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {[
              { icon: "⚡", title: "Pierdes horas generando contenido", desc: "Cotizaciones, correos, publicaciones en redes. Todo manual, todo lento, todo agotador." },
              { icon: "🎯", title: "Tus respuestas no conectan emocionalmente", desc: "Los clientes comparan precios. Tú necesitas vender experiencias. Sin el prompt correcto, siempre pierdes." },
              { icon: "🔄", title: "No sabes qué decirle a la IA", desc: "ChatGPT existe, Gemini existe. Pero sin el prompt correcto, obtienes respuestas genéricas y sin valor." },
              { icon: "📉", title: "La competencia ya usa IA", desc: "Mientras lees esto, otras agencias están automatizando ventas, cerrando clientes y escalando sin contratar personal." },
            ].map(p => (
              <div key={p.title} className="cyber-card p-6 flex gap-4">
                <div style={{ fontSize: 22, flexShrink: 0 }}>{p.icon}</div>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "rgba(232,244,253,0.9)", marginBottom: 6 }}>{p.title}</h3>
                  <p style={{ fontSize: 13, color: "rgba(148,163,184,0.7)", lineHeight: 1.65 }}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-xl text-center" style={{ background: "linear-gradient(135deg,rgba(0,212,255,0.06),rgba(139,92,246,0.06))", border: "1px solid rgba(0,212,255,0.20)" }}>
            <p style={{ fontSize: "clamp(1rem,2vw,1.15rem)", fontWeight: 600, color: "rgba(232,244,253,0.9)", lineHeight: 1.6 }}>
              La solución no es trabajar más —<br />
              <span className="text-neon-cyan">es indicarle a la IA exactamente qué hacer.</span>
            </p>
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          04 · PARA QUIÉN
      ═══════════════════════════════════════ */}
      <section id="beneficios" style={{ background: "#070D1A", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="PERFIL DEL USUARIO" />
          <SectionHeading line1="Este sistema fue diseñado" accent="exactamente para ti" />
          <p style={{ textAlign: "center", color: "rgba(148,163,184,0.7)", marginTop: "1rem", maxWidth: 520, marginInline: "auto", lineHeight: 1.7, marginBottom: "2.5rem" }}>
            Si trabajas en turismo y quieres multiplicar resultados con IA, eres el perfil ideal.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FOR_WHO.yes.map((item: string, i: number) => (
              <div key={i} className="cyber-card p-5 flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5 flex items-center justify-center"
                  style={{ width: 26, height: 26, borderRadius: 7, background: "rgba(0,212,255,0.10)", border: "1px solid rgba(0,212,255,0.25)" }}>
                  <IconCheck />
                </div>
                <span style={{ fontSize: 13, color: "rgba(232,244,253,0.85)", lineHeight: 1.55 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          05 · QUÉ APRENDERÁS
      ═══════════════════════════════════════ */}
      <section style={{ background: "#040810", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="MÓDULO DE APRENDIZAJE" />
          <SectionHeading line1="Lo que dominarás con este" accent="sistema de prompts" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
            {(LEARNS as Learn[]).map((item, i) => (
              <div key={i} className="cyber-card p-5 flex items-start gap-4">
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "rgba(232,244,253,0.92)", marginBottom: 4 }}>{item.title}</div>
                  <p style={{ fontSize: 12, color: "rgba(148,163,184,0.7)", lineHeight: 1.65 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          06 · 20 CAPÍTULOS
      ═══════════════════════════════════════ */}
      <section id="incluye" style={{ background: "#070D1A", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="CONTENIDO DEL EBOOK" />
          <SectionHeading line1="Explora los" accent="20 capítulos especializados" />
          <p style={{ textAlign: "center", color: "rgba(148,163,184,0.7)", marginTop: "1rem", maxWidth: 520, marginInline: "auto", lineHeight: 1.7, marginBottom: "2.5rem" }}>
            Cada capítulo cubre un área crítica del negocio de viajes. De la venta inicial al servicio posventa.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(CHAPTERS as Chapter[]).map((ch, i) => (
              <div key={ch.id} className="cyber-card p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 18 }}>{ch.icon}</span>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "#00D4FF", letterSpacing: "0.1em" }}>
                    CAP. {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <h3 style={{ fontSize: 12, fontWeight: 600, color: "rgba(232,244,253,0.88)", lineHeight: 1.45, minHeight: 34 }}>
                  {ch.title}
                </h3>
                <div style={{ height: 1, background: "rgba(0,212,255,0.07)" }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          07 · PROMPT EXAMPLES (TERMINAL)
      ═══════════════════════════════════════ */}
      <section id="ejemplos" style={{ background: "#040810", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="VISTA PREVIA" />
          <SectionHeading line1="Prompts maestros" accent="en acción" />
          <p style={{ textAlign: "center", color: "rgba(148,163,184,0.7)", marginTop: "1rem", maxWidth: 520, marginInline: "auto", lineHeight: 1.7, marginBottom: "2.5rem" }}>
            Así se ven los prompts que transformarán tu flujo de trabajo.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {(PROMPT_EXAMPLES as PromptEx[]).map((p, i) => (
              <div key={i} className="terminal-window flex flex-col"
                style={{ transition: "box-shadow .25s" }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 0 24px rgba(0,212,255,0.18)")}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}>
                <div className="terminal-bar" style={{ gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840" }} />
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", marginLeft: 6 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "rgba(0,212,255,0.5)" }}>
                      prompt_{p.number}.txt
                    </span>
                    <div className="badge-cyber" style={{ padding: "2px 8px", fontSize: 9 }}>{p.category}</div>
                  </div>
                </div>
                <div className="terminal-body flex flex-col gap-2" style={{ flex: 1 }}>
                  <div style={{ color: "rgba(0,212,255,0.6)", fontSize: 11 }}>// {p.title}</div>
                  <div style={{ color: "#00D4FF", fontSize: 11 }}>MAESTRO&gt; ejecutar prompt</div>
                  <div style={{ marginLeft: 16, lineHeight: 1.8, fontSize: 11, color: "#00FF88", whiteSpace: "pre-line" }}>
                    {p.prompt.length > 200 ? p.prompt.slice(0, 200) + "..." : p.prompt}
                  </div>
                  <div style={{ borderTop: "1px solid rgba(0,212,255,0.07)", paddingTop: 8, marginTop: 4 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "rgba(0,255,136,0.5)" }}>RESULTADO: </span>
                    <span style={{ fontSize: 11, color: "rgba(148,163,184,0.7)" }}>{p.result}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button className="btn-primary-glow" onClick={() => setModalOpen(true)}>
              ⚡ Acceder a los 100 Prompts Completos
            </button>
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          08 · BENEFICIOS
      ═══════════════════════════════════════ */}
      <section style={{ background: "#070D1A", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="RESULTADOS ESPERADOS" />
          <SectionHeading line1="Resultados que los agentes" accent="están reportando" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
            {(BENEFITS as Benefit[]).map((b, i) => {
              const colors = ["#00D4FF", "#8B5CF6", "#00FF88", "#F59E0B"]
              const color = colors[i % colors.length]
              return (
                <div key={i} className="cyber-card p-7 flex flex-col gap-4">
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "clamp(2.5rem,6vw,3.5rem)", fontWeight: 700, color, lineHeight: 1, textShadow: `0 0 24px ${color}50` }}>
                    {b.metric}
                  </div>
                  <p style={{ fontSize: 14, color: "rgba(232,244,253,0.85)", lineHeight: 1.6 }}>{b.label}</p>
                  <div style={{ height: 2, borderRadius: 1, background: `linear-gradient(90deg,${color}50,transparent)` }} />
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          09 · TESTIMONIOS
      ═══════════════════════════════════════ */}
      <section style={{ background: "#040810", padding: "6rem 1rem" }}>
        <div className="max-w-5xl mx-auto px-4">
          <SectionLabel text="PRUEBA SOCIAL" />
          <SectionHeading line1="Lo que dicen los agentes que" accent="ya lo están usando" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            {(TESTIMONIALS as Testimonial[]).map((t, i) => (
              <div key={i} className="cyber-card p-6 flex flex-col gap-4">
                <div className="flex gap-1">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <span key={si} style={{ color: "#F59E0B", fontSize: 14 }}>★</span>
                  ))}
                </div>
                <p style={{ fontSize: 13, color: "rgba(148,163,184,0.8)", lineHeight: 1.75, fontStyle: "italic", flex: 1 }}>
                  "{t.text}"
                </p>
                <div style={{ borderTop: "1px solid rgba(0,212,255,0.08)", paddingTop: "0.875rem" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "rgba(232,244,253,0.9)" }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "#00D4FF", fontFamily: "var(--font-mono)", marginTop: 2 }}>{t.role} · {t.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          10 · FAQ
      ═══════════════════════════════════════ */}
      <section id="faq" style={{ background: "#070D1A", padding: "6rem 1rem" }}>
        <div className="max-w-3xl mx-auto px-4">
          <SectionLabel text="PREGUNTAS FRECUENTES" />
          <SectionHeading line1="¿Tienes dudas?" accent="Aquí las respuestas" />

          <div className="mt-10 flex flex-col gap-2">
            {(FAQS as Faq[]).map((faq, i) => (
              <div key={i} className="cyber-card overflow-hidden" style={{ borderRadius: 10 }}>
                <button
                  className="w-full flex items-center justify-between p-5 text-left"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: "rgba(232,244,253,0.92)", paddingRight: "1rem", lineHeight: 1.4 }}>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#00D4FF", marginRight: 8 }}>{String(i + 1).padStart(2, "0")}.</span>
                    {faq.q}
                  </span>
                  <IconChevron open={faqOpen === i} />
                </button>
                {faqOpen === i && (
                  <div style={{ paddingInline: "1.25rem", paddingBottom: "1.25rem" }}>
                    <div style={{ height: 1, background: "rgba(0,212,255,0.07)", marginBottom: "1rem" }} />
                    <p style={{ fontSize: 13, color: "rgba(148,163,184,0.8)", lineHeight: 1.75 }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-divider" />

      {/* ═══════════════════════════════════════
          11 · CTA FINAL
      ═══════════════════════════════════════ */}
      <section id="cta" style={{ background: "#040810", padding: "7rem 1rem", position: "relative", overflow: "hidden" }}>
        <div className="absolute inset-0 bg-grid-pattern" />
        <div className="orb-cyan absolute" style={{ width: 700, height: 700, top: "50%", left: "50%", transform: "translate(-50%,-50%)", opacity: 0.35 }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center flex flex-col items-center gap-8">
          {/* Testimonial final */}
          <div className="cyber-card p-6 max-w-2xl w-full" style={{ background: "rgba(10,22,40,0.9)" }}>
            <div className="flex gap-1 justify-center mb-3">
              {[1,2,3,4,5].map(s => <span key={s} style={{ color: "#F59E0B" }}>★</span>)}
            </div>
            <p style={{ fontSize: 14, color: "rgba(148,163,184,0.85)", lineHeight: 1.8, fontStyle: "italic" }}>
              "Después de aplicar solo 10 de los 100 prompts, mi tasa de cierre aumentó un 38%. Cada prompt está pensado para el flujo real de trabajo de un agente. Vale cada centavo."
            </p>
            <div style={{ marginTop: "1rem", fontFamily: "var(--font-mono)", fontSize: 11, color: "#00D4FF" }}>
              Carlos Mejía · Agente Senior · Bogotá, Colombia
            </div>
          </div>

          {/* Big headline */}
          <div>
            <div className="badge-gold mb-4">
              ⚡ DESCARGA INMEDIATA · GRATIS
            </div>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem,5vw,3.5rem)", fontWeight: 700, lineHeight: 1.2, color: "rgba(232,244,253,0.97)" }}>
              Empieza hoy a vender<br />
              <span className="text-neon-cyan">con inteligencia artificial</span>
            </h2>
            <p style={{ fontSize: 15, color: "rgba(148,163,184,0.72)", marginTop: "1.25rem", maxWidth: 500, marginInline: "auto", lineHeight: 1.7 }}>
              Únete a más de 2,800 agentes de viajes en 12 países que ya usan estos prompts para multiplicar sus ventas con IA.
            </p>
          </div>

          <button className="btn-primary-glow animate-pulse-glow" style={{ minWidth: 300, fontSize: 14, padding: "18px 44px" }}
            onClick={() => setModalOpen(true)}>
            ⚡ Descargar los 100 Prompts — Gratis
          </button>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-3 justify-center">
            {["🔒 Descarga Segura", "✅ Sin tarjeta de crédito", "⚡ Acceso instantáneo", "🌎 +12 países"].map(b => (
              <div key={b} className="px-4 py-2 rounded-full"
                style={{ background: "rgba(0,212,255,0.06)", border: "1px solid rgba(0,212,255,0.14)", fontSize: 12, color: "rgba(148,163,184,0.72)" }}>
                {b}
              </div>
            ))}
          </div>

          {/* Author card */}
          <div className="cyber-card p-5 max-w-sm w-full" style={{ textAlign: "left" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "rgba(0,212,255,0.5)", letterSpacing: "0.12em", marginBottom: 8 }}>AUTOR</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "rgba(232,244,253,0.92)" }}>Mauricio Polanco Ordóñez</div>
            <div style={{ fontSize: 12, color: "#00D4FF", marginTop: 2 }}>Consultor en IA · Factory One Xperience FOX SAS</div>
            <div style={{ fontSize: 11, color: "rgba(148,163,184,0.5)", marginTop: 4 }}>Bogotá, Colombia · info@consultor-ia.co</div>
            <div className="gold-divider" style={{ marginBlock: "0.75rem" }} />
            <p style={{ fontSize: 11, color: "rgba(148,163,184,0.5)", lineHeight: 1.65 }}>
              Consultor especializado en automatización con IA para el sector turístico en Latinoamérica.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
